import React from 'react';
import { getAllUsers } from '../../../Services/Api/userService';
import { useAuth } from '../../../Context/AuthContext';
import styled from 'styled-components';
import { Button } from '../../UI/Buttons';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  max-width: 560px;
  width: 95%;
  position: relative;
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.4rem;
  cursor: pointer;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.95rem;
  color: #333;
`;

const Input = styled.input`
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 0.95rem;
`;

const Select = styled.select`
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 0.95rem;
  background: #fff;
  cursor: pointer;
`;

const ErrorText = styled.div`
  color: #c0392b;
  font-size: 0.85rem;
  margin-top: 4px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const getUserId = (u) => {
  if (!u) return '';
  return String(u.id || u._id || u.user_id || u.id_user || u.userId || '');
};

const getUserLabel = (u) => {
  if (!u) return '';
  return (
    u.full_name ||
    `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
    u.username ||
    u.name ||
    u.email ||
    getUserId(u) ||
    ''
  );
};

const DepartmentAddModal = ({ isOpen, onClose, onSave, initialData = {} }) => {
  const [name, setName] = React.useState('');
  const [head, setHead] = React.useState('');
  const [users, setUsers] = React.useState([]);
  const [usersLoading, setUsersLoading] = React.useState(false);
  const [usersError, setUsersError] = React.useState(null);
  const [headError, setHeadError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const { user } = useAuth();
  const hasInitialized = React.useRef(false);

  // Initialize form data only when modal opens
  React.useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      const initialHeadId = typeof initialData.head === 'object' 
        ? getUserId(initialData.head) 
        : (initialData.head ? String(initialData.head) : '');
      setName(initialData.name || '');
      setHead(initialHeadId);
      setHeadError('');
      hasInitialized.current = true;
    }
    
    // Reset when modal closes
    if (!isOpen) {
      hasInitialized.current = false;
    }
  }, [isOpen, initialData]);

  React.useEffect(() => {
    if (!isOpen) return;
    const fetchUsers = async () => {
      const companyId = user?.company?.id || user?.company_id || user?.company?.company_id;
      if (!companyId) return;
      setUsersLoading(true);
      setUsersError(null);
      try {
        const list = await getAllUsers(companyId);
        setUsers(Array.isArray(list) ? list : (list?.users || []));
      } catch (err) {
        setUsersError(err?.message || 'Failed to load users');
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHeadError('');
    if (!name.trim()) return;
    if (!head) {
      setHeadError('Please select a head for the department.');
      return;
    }
    if (onSave) {
      setSaving(true);
      try {
        await onSave({ name: name.trim(), head });
        onClose && onClose();
      } finally {
        setSaving(false);
      }
    } else {
      onClose && onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContent>
        <CloseButton onClick={onClose} aria-label="Close">&times;</CloseButton>
        <h3>{initialData && initialData.name ? 'Edit Department' : 'New Department'}</h3>
        <Form onSubmit={handleSubmit} noValidate>
          <Field>
            <Label htmlFor="dept-name">Name</Label>
            <Input
              id="dept-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter department name"
              required
            />
          </Field>

          <Field>
            <Label htmlFor="dept-head">Head (required)</Label>
            <Select
              id="dept-head"
              value={head}
              onChange={(e) => { setHead(e.target.value); setHeadError(''); }}
              required
            >
              <option value="">{usersLoading ? 'Loading users...' : 'Select head'}</option>
              {!usersLoading && users.length === 0 && <option value="" disabled>No users found</option>}
              {!usersLoading && users.map((u) => {
                const id = getUserId(u);
                const label = getUserLabel(u);
                return (
                  <option key={id || label} value={id}>{label}</option>
                );
              })}
            </Select>
            {headError && <ErrorText>{headError}</ErrorText>}
            {usersError && <ErrorText>{usersError}</ErrorText>}
          </Field>

          <Actions>
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Actions>
        </Form>
      </ModalContent>
    </Overlay>
  );
};

export default DepartmentAddModal;
